import { createApi } from '@reduxjs/toolkit/query/react'
import { store } from 'app/store'
import { API_ENDPOINTS } from 'Constants'
import {
  IBgrQuotePayload,
  IBgrQuoteResponse,
  IBgrVerifyCkycResponse,
} from 'Models/BOB/ResponseModels/Bgr/bgrResponse'
import { bgrUrlQuery, getFileHeaders } from 'Services/baseQuery'

export const bgrApi = createApi({
  reducerPath: 'bgrApi',
  baseQuery: bgrUrlQuery,
  endpoints: (builder) => ({
    getBgrQuotations: builder.mutation<IBgrQuoteResponse, any>({
      query: (payload) => ({
        url: API_ENDPOINTS.BOB.BGR_QUOTATIONS,
        method: 'POST',
        body: payload,
      }),
    }),

    getBgrCheckCkyc: builder.mutation<
      IBgrVerifyCkycResponse,
      {
        trace_id: string
        id_type: string
        id_number: string
        insurer_code: string
        plan_name: string
        isFire?: boolean
      }
    >({
      query: (payload) => ({
        url: payload.isFire
          ? API_ENDPOINTS.BOB.FIRE_VERIFY_CKYC
          : API_ENDPOINTS.BOB.BGR_VERIFY_CKYC,
        method: 'POST',
        body: payload,
      }),
    }),
    getBgrStatusCkyc: builder.mutation<
      IBgrVerifyCkycResponse,
      {
        trace_id: string
        insurer_code: string
        plan_name: string
      }
    >({
      query: (payload) => ({
        url: API_ENDPOINTS.BOB.BGR_CKYC_STATUS,
        method: 'POST',
        body: payload,
      }),
    }),

    verifyBgrOtp: builder.mutation<
      IBgrVerifyCkycResponse,
      {
        trace_id: string
        id_type: string
        otp: string
        insurer_code: string
        plan_name: string
        isFire?: boolean
      }
    >({
      query: (payload) => ({
        url: payload.isFire
          ? API_ENDPOINTS.BOB.FIRE_CKYC_OTP_VERIFY
          : API_ENDPOINTS.BOB.BGR_CKYC_OTP_VERIFY,
        method: 'POST',
        body: payload,
      }),
    }),

    getBgrOvdCkyc: builder.mutation<IBgrVerifyCkycResponse, FormData>({
      query: (filePayload: FormData) => {
        let isFire = false
        isFire = filePayload.get('isFire') ? true : false

        const token: string = store.getState().user.authenticatedUser.authtoken ?? ''
        return {
          url: isFire ? API_ENDPOINTS.BOB.FIRE_OVD_CKYC : API_ENDPOINTS.BOB.BGR_OVD_CKYC,
          method: 'POST',
          body: filePayload,
          headers: getFileHeaders(token),
        }
      },
    }),

    bgrPlanQuote: builder.mutation<any, any>({
      query: (payload) => ({
        url: API_ENDPOINTS.BOB.BGR_PLAN_QUOTE,
        method: 'POST',
        body: payload,
      }),
    }),

    bgrDirectDebitMakePayment: builder.mutation<any, any>({
      query: (payload) => ({
        url: API_ENDPOINTS.BOB.DIRECT_DEBIT_MAKE_PAYMENT_BGR,
        method: 'POST',
        body: payload,
      }),
    }),

    bgrDownloadPolicy: builder.mutation<any, any>({
      query: (payload) => ({
        url: API_ENDPOINTS.BOB.DOWNLOAD_POLICY,
        method: 'POST',
        body: payload,
      }),
    }),

    bgrQuoteList: builder.query<any, { trace_id: string }>({
      query: (param) => ({
        url: API_ENDPOINTS.BOB.QUOTE_LISTING,
        method: 'GET',
        params: param,
      }),
    }),

    bgrPolicyStatus: builder.mutation<
      any,
      { trace_id: string; insurer_code: string; plan_name: string }
    >({
      query: (payload) => ({
        url: API_ENDPOINTS.BOB.BGR_POLICY_STATUS,
        method: 'POST',
        body: payload,
      }),
    }),
    bgrSelectPaymentChannel: builder.mutation<
      any,
      { insurer_code: string; trace_id: string; debit_type: string }
    >({
      query: (payload) => ({
        url: API_ENDPOINTS.BOB.SELECT_PAYMENT_CHANNEL_BGR,
        method: 'POST',
        body: payload,
      }),
    }),
  }),
})

export const {
  useGetBgrQuotationsMutation,
  useBgrPlanQuoteMutation,
  useBgrDirectDebitMakePaymentMutation,
  useGetBgrCheckCkycMutation,
  useBgrDownloadPolicyMutation,
  useVerifyBgrOtpMutation,
  useGetBgrOvdCkycMutation,
  useLazyBgrQuoteListQuery,
  useGetBgrStatusCkycMutation,
  useBgrPolicyStatusMutation,
  useBgrSelectPaymentChannelMutation,
} = bgrApi
